import ApplicationController from "./application_controller"
import LineSuggestionsApiService from "../services/line_suggestions_api_service"
import AddLineFilterMixin from "../mixins/add_line_filter_mixin"
import AddLinePaginationMixin from "../mixins/add_line_pagination_mixin"
import AddLineDataFormatter from "../utils/add_line_data_formatter"
import AddLineRowTemplate from "../templates/add_line_row_template"
import AddLineDropdownTemplate from "../templates/add_line_dropdown_template"

export default class AddLineModalController extends ApplicationController {
  // ==========================================
  // STATIC PROPERTIES
  // ==========================================

  static targets = [
    "container", "content", "linesList", "pagination",
    "searchInput", "categoryDropdown", "categoryButton", "categoryButtonText",
    "categoryDropdownMenu", "categorySearchInput", "categorySeparator", "clearCategoriesLink", "categoryList",
    "exchangeDropdown", "exchangeButton", "exchangeButtonText",
    "exchangeDropdownMenu", "exchangeSearchInput", "exchangeSeparator", "clearExchangesLink", "exchangeList",
    "adTypeDropdown", "adTypeButton", "adTypeButtonText",
    "adTypeDropdownMenu", "adTypeSearchInput", "adTypeSeparator", "clearAdTypesLink", "adTypeList",
    "impTypeDropdown", "impTypeButton", "impTypeButtonText",
    "impTypeDropdownMenu", "impTypeSearchInput", "impTypeSeparator", "clearAdTypesLink", "impTypeList",
    "exchangeSelect", "statusSelect", "pageSizeSelect", "pageSizeContainer",
    "selectedCount", "selectedCountHeader", "loadingIndicator", "preloaderText",
    "tableContainer", "emptyState", "paginationTop", "selectAllIcon",
    "autoPricesCheckbox", "bidPriceField", "dailyBudgetField", "maxBudgetField",
    "bidPriceInput", "dailyBudgetInput", "maxBudgetInput",
    "sortIconDealId", "selectAllButton", "select100Button", "select500Button", "select1000Button"
  ]

  static values = {
    lineSuggestions: { type: Array, default: [] },
    filteredLines: { type: Array, default: [] },
    visibleLines: { type: Array, default: [] },
    selectedLines: { type: Array, default: [] },
    selectedLineObjects: { type: Array, default: [] },
    currentPage: { type: Number, default: 0 },
    linesPerPage: { type: Number, default: 100 },
    totalLines: { type: Number, default: 0 },
    linesReady: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    campaignId: Number,
    sortField: { type: String, default: "trafficNumber" },
    sortDirection: { type: String, default: "desc" },
    selectedCategories: { type: Array, default: [] },
    availableCategories: { type: Array, default: [] },
    filteredCategories: { type: Array, default: [] },
    allCategories: { type: Array, default: [] },
    selectedExchanges: { type: Array, default: [] },
    availableExchanges: { type: Array, default: [] },
    filteredExchanges: { type: Array, default: [] },
    allExchanges: { type: Array, default: [] },
    selectedAdTypes: { type: Array, default: [] },
    availableAdTypes: { type: Array, default: [] },
    filteredAdTypes: { type: Array, default: [] },
    allAdTypes: { type: Array, default: [] },
    creativeAdTypes: { type: Array, default: [] },
    selectedImpTypes: { type: Array, default: [] },
    availableImpTypes: { type: Array, default: [] },
    filteredImpTypes: { type: Array, default: [] },
    allImpTypes: { type: Array, default: [] },
    selectionState: { type: String, default: 'none' }
  }

  // ==========================================
  // LIFECYCLE METHODS
  // ==========================================

  connect() {
    this._initializeCampaignId()
    this._setupEventHandlers()
    this._initializeApi()
    this._initializeLists()
    AddLineFilterMixin.initializeFilterState.call(this)
  }

  disconnect() {
    this._removeEventHandlers()
    this.close()
  }

  // ==========================================
  // PUBLIC METHODS - Modal Control
  // ==========================================

  open(event) {
    try {
      event?.preventDefault()

      if (!this.hasContainerTarget) {
        console.error('Container target not found!')
        return
      }

      this._showModal()
      this._setupModalEventListeners()
      this.loadLineSuggestions("Loading line suggestions...")
      this.dispatch("add-line-modal:opened")
    } catch (error) {
      console.error('Error opening modal:', error)
      alert(`Error opening modal: ${error.message}`)
    }
  }

  close(event) {
    event?.preventDefault()
    this._hideModal()
    this._removeModalEventListeners()
    this.dispatch("add-line-modal:closed")
  }

  ensureModalContentInjected() {
    if (this.containerTarget && !this.containerTarget.querySelector('.modal-content')) {
      if (typeof window.injectModalContent === 'function') {
        window.injectModalContent()
      }
    }
  }

  // ==========================================
  // PUBLIC METHODS - Data Management
  // ==========================================

  async loadLineSuggestions(loadingText = "Loading line suggestions...") {
    const safeLoadingText = typeof loadingText === 'string' ? loadingText : "Loading line suggestions..."
    this.setLoading(true, safeLoadingText)

    try {
      const params = this._buildRequestParams()
      const data = await this.apiService.fetchLineSuggestions(params)

      this._updateDataFromResponse(data)
      this.updatePagination()

      // Force update dropdowns to show/hide clear links
      this.updateCategoryDropdown()
      this.updateExchangeDropdown()
      this.updateAdTypeDropdown()
      
      // Update selection button states
      this.updateSelectionButtonStates()

      // Handle pending selection after data loads
      if (this.pendingSelectionCount) {
        const requestedCount = this.pendingSelectionCount
        this.pendingSelectionCount = null
        
        const selectedIds = this._getAvailableLineIds(this.visibleLinesValue, requestedCount)
        const selectedLineObjects = this.visibleLinesValue.filter(line => selectedIds.includes(line.id))
        
        if (selectedIds.length < requestedCount && this.currentPageValue < this.totalPages - 1) {
          this._continueSelectionWithPagination(requestedCount, selectedIds, selectedLineObjects)
        } else {
          this.selectedLinesValue = selectedIds
          this.selectedLineObjectsValue = selectedLineObjects
          this.updateSelectAllIcon()
          this.renderLines()
          this.updateSelectionButtonStates()
          
          const appliedInLoadedData = this.visibleLinesValue.filter(line => line.planStatus === 'active').length
          const addedCount = selectedIds.length
          const totalCount = appliedInLoadedData + addedCount
          this.showSuccess(`Selected ${addedCount} lines`)
        }
      } else if (this._accumulatedSelection && this._remainingCount) {
        const additionalIds = this._getAvailableLineIds(this.visibleLinesValue, this._remainingCount)
        const combinedSelection = [...this._accumulatedSelection, ...additionalIds]
        
        const additionalLineObjects = this.visibleLinesValue.filter(line => 
          additionalIds.includes(line.id) && 
          !this._accumulatedLineObjects.some(obj => obj.id === line.id)
        )
        const combinedLineObjects = [...this._accumulatedLineObjects, ...additionalLineObjects]
        
        if (combinedSelection.length < this._targetSelectionCount && this.currentPageValue < this.totalPages - 1) {
          this._continueSelectionWithPagination(this._targetSelectionCount, combinedSelection, combinedLineObjects)
        } else {
          this.selectedLinesValue = combinedSelection
          this.selectedLineObjectsValue = combinedLineObjects
          this.updateSelectAllIcon()
          this.renderLines()
          this.updateSelectionButtonStates()
          
          this._accumulatedSelection = null
          this._accumulatedLineObjects = null
          this._targetSelectionCount = null
          this._remainingCount = null
          
          const appliedInLoadedData = this.visibleLinesValue.filter(line => line.planStatus === 'active').length
          const addedCount = combinedSelection.length
          const totalCount = appliedInLoadedData + addedCount
          this.showSuccess(`Selected ${addedCount} lines`)
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return

      console.error('Error in loadLineSuggestions:', error)
      this.showError('Failed to load line suggestions')
      this._resetData()
    } finally {
      this.setLoading(false)
    }
  }

  updatePagination() {
    this.visibleLinesValue = this.lineSuggestionsValue
    this.renderLines()
    AddLinePaginationMixin.renderPaginationControls.call(this)
  }

  renderLines() {
    if (!this.hasLinesListTarget) return

    const html = this.visibleLinesValue.map(line => 
      AddLineRowTemplate.render.call(this, line)
    ).join('')

    this.linesListTarget.innerHTML = html
    this.updateSelectedCount()
    this.updateSortIcons()
    this.updateSelectionButtonStates()
  }

  // ==========================================
  // PUBLIC METHODS - Event Handlers
  // ==========================================

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  handleBackdropClick(event) {
    if (event.target === this.containerTarget) {
      this.close()
    }
  }

  toggleLine(event) {
    const checkbox = event.target
    const lineUuid = event.currentTarget.dataset.lineUuid
    const line = this.visibleLinesValue.find(l => l.id === lineUuid)
    const planStatus = line?.planStatus || line?.['plan-status']
    const isDisabled = planStatus === 'active'

    if (isDisabled) {
      event.preventDefault()
      return
    }

    if (checkbox.checked) {
      if (!this.selectedLinesValue.includes(lineUuid)) {
        this.selectedLinesValue = [...this.selectedLinesValue, lineUuid]
        if (line) {
          this.selectedLineObjectsValue = [...this.selectedLineObjectsValue, line]
        }
      }
    } else {
      this.selectedLinesValue = this.selectedLinesValue.filter(uuid => uuid !== lineUuid)
      this.selectedLineObjectsValue = this.selectedLineObjectsValue.filter(obj => obj.id !== lineUuid)
    }

    this.updateSelectedCount()
  }

  toggleSelectAll(event) {
    event?.stopPropagation()

    const availableLines = this.visibleLinesValue
      .filter(line => line.planStatus !== 'active')

    if (event.target.checked) {
      const availableUuids = availableLines.map(line => line.id)
      this.selectedLinesValue = [...new Set([...this.selectedLinesValue, ...availableUuids])]
      const newLineObjects = availableLines.filter(line => !this.selectedLineObjectsValue.some(obj => obj.id === line.id))
      this.selectedLineObjectsValue = [...this.selectedLineObjectsValue, ...newLineObjects]
    } else {
      const availableUuids = availableLines.map(line => line.id)
      this.selectedLinesValue = this.selectedLinesValue.filter(uuid => !availableUuids.includes(uuid))
      this.selectedLineObjectsValue = this.selectedLineObjectsValue.filter(obj => !availableUuids.includes(obj.id))
    }

    this.renderLines()
  }

  _selectTopN(count) {
    this.linesPerPageValue = count
    this.currentPageValue = 0
    this.pendingSelectionCount = count
    if (this.hasPageSizeSelectTarget) {
      this.pageSizeSelectTarget.value = String(count)
    }
    this.loadLineSuggestions(`Loading ${count} lines...`)
  }

  _getAvailableLineIds(lines, limit = null) {
    const available = lines.filter(line => line.planStatus !== 'active')
    const unselected = available.filter(line => !this.selectedLinesValue.includes(line.id))
    const toSelect = limit ? unselected.slice(0, limit) : unselected
    return toSelect.map(line => line.id)
  }

  selectAll() {
    const availableLines = this._getAvailableLineIds(this.visibleLinesValue)
    this.selectedLinesValue = [...new Set([...this.selectedLinesValue, ...availableLines])]
    
    // Store line objects for newly selected lines
    const newLineObjects = this.visibleLinesValue.filter(line => 
      availableLines.includes(line.id) && 
      !this.selectedLineObjectsValue.some(obj => obj.id === line.id)
    )
    this.selectedLineObjectsValue = [...this.selectedLineObjectsValue, ...newLineObjects]
    
    this.updateSelectAllIcon()
    this.renderLines()
  }

  select100() {
    this._setSelectionState('100')
  }

  select500() {
    this._setSelectionState('500')
  }
  
  select1000() {
    this._setSelectionState('1000')
  }

  clearSelection() {
    this.selectedLinesValue = []
    this.selectedLineObjectsValue = []
    this.selectionStateValue = 'none'
    this.updateSelectAllIcon()
    this.renderLines()
    this.updateSelectionButtonStates()
  }

  _setSelectionState(state) {
    this.selectionStateValue = state
    this.selectedLinesValue = []
    this.selectedLineObjectsValue = []
    
    let pageSize = 100
    if (state === '1000') {
      pageSize = 1000
    } else if (state === '500') {
      pageSize = 500
    } else if (state === '100') {
      pageSize = 100
    }
    
    this._originalPageSize = pageSize
    this.linesPerPageValue = pageSize
    this.currentPageValue = 0
    this.pendingSelectionCount = parseInt(state)
    
    if (this.hasPageSizeSelectTarget) {
      this.pageSizeSelectTarget.value = String(pageSize)
    }
    
    this.loadLineSuggestions(`Loading ${state} lines...`)
  }

  _continueSelectionWithPagination(requestedCount, currentSelection, currentLineObjects = []) {
    this._accumulatedSelection = currentSelection
    this._accumulatedLineObjects = currentLineObjects
    this._targetSelectionCount = requestedCount
    this._remainingCount = requestedCount - currentSelection.length
    
    this.currentPageValue++
    this.loadLineSuggestions(`Loading more lines... (${currentSelection.length}/${requestedCount} selected)`)
  }

  performSearch() {
    const searchText = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ''

    if (searchText === '') {
      this.currentPageValue = 0
      this.loadLineSuggestions('Loading...')
      return
    }

    if (searchText.length < 3) {
      this.showError('Please enter at least 3 characters to search')
      return
    }

    this.currentPageValue = 0
    this.loadLineSuggestions('Searching...')
  }

  onSearchKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.performSearch()
    }
  }

  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
      this.currentPageValue = 0
      this.loadLineSuggestions('Clearing search...')
    }
  }

  toggleAutoPrices(event) {
    const isChecked = event.target.checked
    const fields = [
      this.hasBidPriceFieldTarget ? this.bidPriceFieldTarget : null,
      this.hasDailyBudgetFieldTarget ? this.dailyBudgetFieldTarget : null,
      this.hasMaxBudgetFieldTarget ? this.maxBudgetFieldTarget : null
    ].filter(field => field)

    fields.forEach(field => {
      if (isChecked) {
        field.classList.add('hidden')
      } else {
        field.classList.remove('hidden')
      }
    })

    if (isChecked) {
      if (this.hasBidPriceInputTarget) this.bidPriceInputTarget.value = ''
      if (this.hasDailyBudgetInputTarget) this.dailyBudgetInputTarget.value = ''
      if (this.hasMaxBudgetInputTarget) this.maxBudgetInputTarget.value = ''
    }
  }

  sortBy(event) {
    const field = event.target.closest('[data-field]').dataset.field

    if (this.sortFieldValue === field) {
      this.sortDirectionValue = this.sortDirectionValue === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortFieldValue = field
      this.sortDirectionValue = ['trafficNumber'].includes(field) ? 'desc' : 'asc'
    }

    this.updateSortIcons()
    this.loadLineSuggestions('Sorting data...')
  }

  async addSelectedLines() {
    if (this.selectedLinesValue.length === 0) {
      this.showError('Please select at least one line')
      return
    }

    const selectedLines = this.selectedLineObjectsValue.filter(line =>
      this.selectedLinesValue.includes(line.id)
    )

    this.setLoading(true, "Adding selected lines...")

    try {
      const autoPrices = this.hasAutoPricesCheckboxTarget ? this.autoPricesCheckboxTarget.checked : true
      let customValues = {}

      if (!autoPrices) {
        customValues = {
          "bidPrice": this.hasBidPriceInputTarget ? parseFloat(this.bidPriceInputTarget.value) || null : null,
          "dailyBudget": this.hasDailyBudgetInputTarget ? parseFloat(this.dailyBudgetInputTarget.value) || null : null,
          "maxBudget": this.hasMaxBudgetInputTarget ? parseFloat(this.maxBudgetInputTarget.value) || null : null
        }
      }

      const result = await this.apiService.addLines(selectedLines, autoPrices, customValues)
      const data = result.data || {}
      const attributes = data.attributes || {}

      this.showSuccess(attributes.message || `Successfully added ${this.selectedLinesValue.length} lines`)

      this.dispatch("add-line-modal:lines-added", {
        detail: {
          addedLines: attributes.addedCount || this.selectedLinesValue.length,
          duplicatesCount: attributes.duplicatesCount || 0,
          result: result
        }
      })

      setTimeout(() => {
        this.close()
      }, 2000)

    } catch (error) {
      const errorMessage = error.message || 'Failed to add selected lines'
      this.showError(errorMessage)
    } finally {
      this.setLoading(false)
    }
  }

  // ==========================================
  // PUBLIC METHODS - UI Updates
  // ==========================================

  updateSelectedCount() {
    const count = this.selectedLinesValue.length
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = count
    }
    if (this.hasSelectedCountHeaderTarget) {
      this.selectedCountHeaderTarget.textContent = count
    }
    this.updateSelectAllIcon()
  }

  updateSelectAllIcon() {
    if (!this.hasSelectAllIconTarget) return

    const availableLines = this.visibleLinesValue
      .filter(line => line.planStatus !== 'active')

    const selectedAvailableLines = availableLines
      .filter(line => this.selectedLinesValue.includes(line.id))

    const allSelected = availableLines.length > 0 && selectedAvailableLines.length === availableLines.length
    const someSelected = selectedAvailableLines.length > 0

    if (allSelected) {
      this.selectAllIconTarget.checked = true
      this.selectAllIconTarget.indeterminate = false
    } else if (someSelected) {
      this.selectAllIconTarget.checked = false
      this.selectAllIconTarget.indeterminate = true
    } else {
      this.selectAllIconTarget.checked = false
      this.selectAllIconTarget.indeterminate = false
    }
  }

  updateSortIcons() {
    const allHeaders = document.querySelectorAll('[data-field] i')
    allHeaders.forEach(icon => {
      icon.className = 'fa fa-sort'
    })

    const activeHeader = document.querySelector(`[data-field="${this.sortFieldValue}"] i`)
    if (activeHeader) {
      activeHeader.className = this.sortDirectionValue === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'
    }
  }

  updateSelectionButtonStates() {
    if (!this.lineSuggestionsValue || this.lineSuggestionsValue.length === 0) return

    const currentState = this.selectionStateValue
    
    this._updateButtonState('selectAllButton', false)
    this._updateButtonState('select100Button', currentState === '100')
    this._updateButtonState('select500Button', currentState === '500')
    this._updateButtonState('select1000Button', currentState === '1000')
  }

  _updateButtonState(buttonTarget, isActive) {
    if (!this[`has${buttonTarget.charAt(0).toUpperCase() + buttonTarget.slice(1)}Target`]) return

    const button = this[`${buttonTarget}Target`]
    if (!button) return

    if (isActive) {
      button.classList.remove('btn-default', 'btn-primary')
      button.classList.add('btn-success')
      button.disabled = false
    } else {
      button.classList.remove('btn-success')
      button.classList.add('btn-primary')
      button.disabled = false
    }
  }

  setLoading(loading, text = 'Loading...') {
    this.isLoadingValue = loading

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('hidden', !loading)
    }

    if (this.hasPreloaderTextTarget) {
      this.preloaderTextTarget.textContent = text
    }

    this.togglePaginationButtons(!loading)
    this.updateVisibility()
  }

  togglePaginationButtons(enabled) {
    const paginationContainer = this.hasPaginationTopTarget ? this.paginationTopTarget : null
    if (paginationContainer) {
      const buttons = paginationContainer.querySelectorAll('a, button')
      buttons.forEach(button => {
        if (enabled) {
          button.classList.remove('disabled')
          button.style.pointerEvents = 'auto'
          button.style.opacity = '1'
        } else {
          button.classList.add('disabled')
          button.style.pointerEvents = 'none'
          button.style.opacity = '0.5'
        }
      })
    }
  }

  updateVisibility() {
    const hasData = this.linesReadyValue && this.lineSuggestionsValue.length > 0 && !this.isLoadingValue
    const isLoading = this.isLoadingValue
    const isEmpty = this.linesReadyValue && this.lineSuggestionsValue.length === 0 && !this.isLoadingValue

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('hidden', !isLoading)
    }

    if (this.hasTableContainerTarget) {
      this.tableContainerTarget.classList.toggle('hidden', !hasData)
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('hidden', !isEmpty)
    }

    if (this.hasPaginationTopTarget) {
      this.paginationTopTarget.classList.toggle('hidden', !hasData)
    }

    if (this.hasPageSizeContainerTarget && isLoading) {
      this.pageSizeContainerTarget.classList.add('hidden')
    }
  }

  showError(message) {
    alert(`Error: ${message}`)
  }

  showSuccess(message) {
    alert(`Success: ${message}`)
  }

  // ==========================================
  // PUBLIC METHODS - Data Formatters
  // ==========================================

  formatNumber(number) {
    return AddLineDataFormatter.formatNumber(number)
  }

  getCategoryName(categoryId) {
    return AddLineDataFormatter.getCategoryName(categoryId)
  }

  getExchangeName(exchangeId) {
    return AddLineDataFormatter.getExchangeName(exchangeId)
  }

  getAdTypeName(adType) {
    return AddLineDataFormatter.getAdTypeName(adType)
  }

  getImpTypeName(impType) {
    return AddLineDataFormatter.getAdTypeName(impType)
  }

  getHistoryStatusText(line) {
    return AddLineDataFormatter.getHistoryStatusText(line)
  }

  getStoreUrl(appId) {
    return AddLineDataFormatter.getStoreUrl(appId)
  }

  getBuuid() {
    return AddLineDataFormatter.getBuuid()
  }

  getCategoryDisplayName(categoryId) {
    return AddLineDataFormatter.getCategoryName(categoryId) || categoryId
  }

  // ==========================================
  // GETTERS
  // ==========================================

  get totalPages() {
    return Math.ceil(this.totalLinesValue / this.linesPerPageValue)
  }

  // ==========================================
  // PRIVATE METHODS
  // ==========================================

  _initializeCampaignId() {
    if (!this.campaignIdValue || isNaN(this.campaignIdValue)) {
      const urlMatch = window.location.pathname.match(/\/campaigns\/(\d+)/)
      if (urlMatch) {
        this.campaignIdValue = parseInt(urlMatch[1])
      }
    }
  }

  _setupEventHandlers() {
    this.boundKeyHandler = this.handleKeydown.bind(this)
    this.boundBackdropClick = this.handleBackdropClick.bind(this)
    this.boundDocumentClick = AddLineFilterMixin.handleDocumentClick.bind(this)
  }

  _removeEventHandlers() {
    document.removeEventListener('keydown', this.boundKeyHandler)
    if (this.containerTarget) {
      this.containerTarget.removeEventListener('click', this.boundBackdropClick)
    }
    document.removeEventListener('click', this.boundDocumentClick)
  }

  _initializeApi() {
    const buuid = AddLineDataFormatter.getBuuid()
    this.apiService = new LineSuggestionsApiService(buuid, this.campaignIdValue)
  }

  _initializeLists() {
    this.exchanges = []
    this.categories = []
    this.statuses = [
      { value: '', label: 'All' },
      { value: 'in_plan', label: 'In the Plan' },
      { value: 'was_in_plan', label: 'Was in the Plan' },
      { value: 'new_traffic', label: 'New Traffic' }
    ]

    this._initializeClearLinks()
  }

  _initializeClearLinks() {
    if (this.hasClearCategoriesLinkTarget) {
      this.clearCategoriesLinkTarget.classList.add('hidden')
    }
    if (this.hasClearExchangesLinkTarget) {
      this.clearExchangesLinkTarget.classList.add('hidden')
    }
    if (this.hasClearAdTypesLinkTarget) {
      this.clearAdTypesLinkTarget.classList.add('hidden')
    }
    if (this.hasClearImpTypesLinkTarget) {
      this.clearImpTypesLinkTarget.classList.add('hidden')
    }
    if (this.hasCategorySeparatorTarget) {
      this.categorySeparatorTarget.classList.add('hidden')
    }
    if (this.hasExchangeSeparatorTarget) {
      this.exchangeSeparatorTarget.classList.add('hidden')
    }
    if (this.hasAdTypeSeparatorTarget) {
      this.adTypeSeparatorTarget.classList.add('hidden')
    }
    if (this.hasImpTypeSeparatorTarget) {
      this.impTypeSeparatorTarget.classList.add('hidden')
    }
  }

  _showModal() {
    this.ensureModalContentInjected()
    this.containerTarget.classList.remove('hidden')
    this.containerTarget.style.display = 'block'
    this.containerTarget.style.zIndex = '1050'
    this.containerTarget.classList.add('in')

    if (!document.querySelector('.modal-backdrop')) {
      const backdrop = document.createElement('div')
      backdrop.className = 'modal-backdrop fade in'
      document.body.appendChild(backdrop)
    }

    document.body.classList.add('modal-open')
  }

  _hideModal() {
    this.containerTarget.classList.add('hidden')
    this.containerTarget.style.display = 'none'
    this.containerTarget.classList.remove('in')

    const backdrop = document.querySelector('.modal-backdrop')
    if (backdrop) {
      backdrop.remove()
    }

    document.body.classList.remove('modal-open')
  }

  _setupModalEventListeners() {
    document.addEventListener('keydown', this.boundKeyHandler)
    this.containerTarget.addEventListener('click', this.boundBackdropClick)
    document.addEventListener('click', this.boundDocumentClick)
  }

  _removeModalEventListeners() {
    document.removeEventListener('keydown', this.boundKeyHandler)
    this.containerTarget.removeEventListener('click', this.boundBackdropClick)
  }

  _buildRequestParams() {
    const params = {}

    const sortParam = AddLineDataFormatter.buildSortParam(this.sortFieldValue, this.sortDirectionValue)
    if (sortParam) {
      params.sort = sortParam
    }

    const pageParam = AddLinePaginationMixin.buildPageParam.call(this)
    if (pageParam) {
      params.page = pageParam
    }

    const filterParam = AddLineFilterMixin.buildFilterParam.call(this)
    if (filterParam && Object.keys(filterParam).length > 0) {
      params.filter = filterParam
    }

    return params
  }

  _updateDataFromResponse(data) {
    this.lineSuggestionsValue = data.lineSuggestions
    this.totalLinesValue = data.totalCount
    this.linesReadyValue = true
    this.updateFiltersLists(data.lineSuggestions)

    if (data.categoryCounts) {
      this.updateCategoryCounts(data.categoryCounts)
    } else {
      this.updateCategoryDropdown()
    }

    if (data.exchangeCounts) {
      this.updateExchangeCounts(data.exchangeCounts)
    }

    if (data.adTypeCounts) {
      this.updateAdTypeCounts(data.adTypeCounts)
    }

    if (data.impTypeCounts) {
      this.updateImpTypeCounts(data.impTypeCounts)
    }

    if (data.availableAdTypes) {
      this.updateAvailableAdTypes(data.availableAdTypes)
    }
  }

  _resetData() {
    this.lineSuggestionsValue = []
    this.totalLinesValue = 0
    this.linesReadyValue = true
    this.updatePagination()
  }
}

// ==========================================
// MIXINS
// ==========================================

Object.assign(AddLineModalController.prototype, AddLineFilterMixin)
Object.assign(AddLineModalController.prototype, AddLinePaginationMixin)
